import { IonSkeletonText } from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import ActionIconButton from "@components/buttons/ActionIconButton";
import ArticleCard from "@components/cards/ArticleCard";
import styles from "@components/carePlan/CarePlan.module.css";
import CarePlanActionItem from "@components/carePlan/CarePlanActionItem";
import ExpertService from "@components/carePlan/ExpertService";
import GoalEditorSection from "@components/carePlan/GoalEditorSection";
import GoalEditorTextField from "@components/carePlan/GoalEditorTextField";
import ItemBrowser from "@components/carePlan/ItemBrowser";
import Tool, { ToolType } from "@components/carePlan/Tool";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import EmployerBenefitCard from "@components/explore/EmployerBenefitCard";
import SearchInput from "@components/fieldsNative/SearchInput";
import CarePlanActionItemForm from "@components/forms/CarePlanActionItemForm";
import TaskForm from "@components/forms/TaskForm";
import LegalDocumentTracker from "@components/LegalDocumentTracker";
import MedicationTracker from "@components/MedicationTracker";
import Modal, { ModalElement } from "@components/Modal";
import ProviderTracker from "@components/ProviderTracker";
import Heading2 from "@components/typography/Heading2";
import useCreateGoalItem from "@hooks/mutations/useCreateGoalItem";
import useCreateWorksheet from "@hooks/mutations/useCreateWorksheet";
import useDestroyGoal from "@hooks/mutations/useDestroyGoal";
import useDestroyWorksheet from "@hooks/mutations/useDestroyWorksheet";
import useRemoveGoalItem from "@hooks/mutations/useRemoveGoalItem";
import useOpener from "@hooks/useOpener";
import useSearchArticles from "@hooks/useSearchArticles";
import useSearchEmployerBenefits from "@hooks/useSearchEmployerBenefits";
import useSearchWorksheetTemplates from "@hooks/useSearchWorksheetTemplates";
import { CarePlanActionItemFragment, GetJourneyWithPlanQuery, GoalItemTypeEnum, TaskFragment } from "@typing/Generated";
import { groupTools, medicationsBatched, providersBatched, sortByPosition, sortByTitle } from "@utils/modelUtils";

type Props = {
  goal?: GetJourneyWithPlanQuery["journey"]["goals"][0] | null;
  journey: GetJourneyWithPlanQuery["journey"];
  loading: boolean;
  setSelectedGoalId: (id: string) => void;
};

type Task = TaskFragment;

const GoalEditor = ({ goal, journey, loading, setSelectedGoalId }: Props) => {
  const intl = useIntl();
  const history = useHistory();
  const [articleSearch, setArticleSearch] = useState("");
  const [employerBenefitSearch, setEmployerBenefitSearch] = useState("");
  const goalDeleteOpener = useOpener();
  const legalOpener = useOpener();
  const medicationTrackerOpener = useOpener();
  const providerOpener = useOpener();
  const [toolSearch, setToolSearch] = useState("");
  const titleOpener = useOpener(goal ? false : true);

  useEffect(() => {
    if (goal) {
      titleOpener.close();
    } else {
      titleOpener.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal?.id]);
  const actionItemOpener = useOpener();
  const taskOpener = useOpener();
  const [actionItemBeingEdited, setActionItemBeingEdited] = useState<CarePlanActionItemFragment | undefined>(undefined);
  const [taskBeingEdited, setTaskBeingEdited] = useState<Task | undefined>(undefined);
  const actionItemModalRef = useRef<ModalElement>(null);
  const taskModalRef = useRef<ModalElement>(null);

  const articleBrowserOpener = useOpener();
  const carePlanActionItemBrowserOpener = useOpener();
  const employerBenefitBrowserOpener = useOpener();
  const taskBrowserOpener = useOpener();
  const worksheetBrowserOpener = useOpener();
  const goalTemplateBrowserOpener = useOpener();

  const articleSectionOpener = useOpener();
  const carePlanActionItemSectionOpener = useOpener();
  const taskSectionOpener = useOpener();
  const worksheetSectionOpener = useOpener();

  const handleActionItemError = () => {
    if (actionItemModalRef.current) {
      actionItemModalRef.current.scrollToTop();
    }
  };

  const batchedProviders = useMemo(() => providersBatched(journey.providers), [journey.providers]);
  const batchedMedications = useMemo(() => medicationsBatched(journey.medications), [journey.medications]);

  const onActionItemEdit = (actionItem: CarePlanActionItemFragment) => {
    setActionItemBeingEdited(actionItem);
    actionItemOpener.open();
  };

  const tools = useMemo(() => {
    if (goal) {
      const groups = groupTools({
        carePlanActionItems: goal.carePlanActionItems,
        goals: [goal],
        journey,
        worksheets: goal.worksheets
      });
      return groups.new.concat(groups.inProgress).concat(groups.completed);
    }
    return [];
  }, [goal, journey]);

  const onTaskEdit = (task: Task) => {
    setTaskBeingEdited(task);
    taskOpener.open();
  };

  const removeGoalItem = useRemoveGoalItem(goal ?? null);

  const worksheetCreate = useCreateWorksheet(journey);
  const worksheetDestroy = useDestroyWorksheet(journey);

  const addGoalItem = useCreateGoalItem(goal ?? null);

  const searchArticles = useSearchArticles(journey.id, id =>
    addGoalItem({ articleId: id, goalId: goal?.id ?? "", itemType: GoalItemTypeEnum.ARTICLE })
  );

  const onEmployerBenefitSearch = useSearchEmployerBenefits(journey.id, id =>
    addGoalItem({ employerBenefitId: id, goalId: goal?.id ?? "", itemType: GoalItemTypeEnum.EMPLOYER_BENEFIT })
  );
  const onToolSearch = useSearchWorksheetTemplates(
    true,
    id =>
      worksheetCreate({
        variables: {
          goalId: goal?.id ?? "",
          journeyId: journey.id,
          worksheetTemplateId: id
        }
      }),
    tool => addGoalItem({ goalId: goal?.id ?? "", itemType: GoalItemTypeEnum.TOOL, tool })
  );

  const handleArticleRemove = (articleId: string) => {
    if (goal) {
      removeGoalItem({
        articleId,
        goalId: goal.id
      });
    }
  };

  const handleEmployerBenefitRemove = (employerBenefitId: string) => {
    if (goal) {
      removeGoalItem({
        employerBenefitId,
        goalId: goal.id
      });
    }
  };

  const handleToolRemove = (tool: ToolType) => {
    if (goal) {
      if (tool.worksheet) {
        worksheetDestroy({
          id: tool.worksheet.id
        });
      } else {
        removeGoalItem({
          goalId: goal.id,
          tool: tool.type
        });
      }
    }
  };

  const goalDestroy = useDestroyGoal(journey);

  const handleGoalDelete = () => {
    if (goal) {
      goalDestroy({
        id: goal.id
      });
    }
  };

  useEffect(() => {
    if (articleBrowserOpener.isOpen) {
      carePlanActionItemBrowserOpener.close();
      employerBenefitBrowserOpener.close();
      taskBrowserOpener.close();
      worksheetBrowserOpener.close();
      goalTemplateBrowserOpener.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleBrowserOpener.isOpen]);

  useEffect(() => {
    if (carePlanActionItemBrowserOpener.isOpen) {
      articleBrowserOpener.close();
      employerBenefitBrowserOpener.close();
      taskBrowserOpener.close();
      worksheetBrowserOpener.close();
      goalTemplateBrowserOpener.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carePlanActionItemBrowserOpener.isOpen]);

  useEffect(() => {
    if (goalTemplateBrowserOpener.isOpen) {
      articleBrowserOpener.close();
      carePlanActionItemBrowserOpener.close();
      employerBenefitBrowserOpener.close();
      taskBrowserOpener.close();
      worksheetBrowserOpener.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalTemplateBrowserOpener.isOpen]);

  useEffect(() => {
    if (employerBenefitBrowserOpener.isOpen) {
      articleBrowserOpener.close();
      carePlanActionItemBrowserOpener.close();
      taskBrowserOpener.close();
      worksheetBrowserOpener.close();
      goalTemplateBrowserOpener.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerBenefitBrowserOpener.isOpen]);

  useEffect(() => {
    if (taskBrowserOpener.isOpen) {
      articleBrowserOpener.close();
      carePlanActionItemBrowserOpener.close();
      employerBenefitBrowserOpener.close();
      worksheetBrowserOpener.close();
      goalTemplateBrowserOpener.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskBrowserOpener.isOpen]);

  useEffect(() => {
    if (worksheetBrowserOpener.isOpen) {
      articleBrowserOpener.close();
      carePlanActionItemBrowserOpener.close();
      employerBenefitBrowserOpener.close();
      taskBrowserOpener.close();
      goalTemplateBrowserOpener.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksheetBrowserOpener.isOpen]);

  useEffect(() => {
    if (
      articleBrowserOpener.isOpen ||
      carePlanActionItemBrowserOpener.isOpen ||
      employerBenefitBrowserOpener.isOpen ||
      taskBrowserOpener.isOpen ||
      worksheetBrowserOpener.isOpen ||
      goalTemplateBrowserOpener.isOpen
    ) {
      history.replace({ search: "hideFab=true" });
    } else {
      history.replace({ search: "" });
    }
  }, [
    articleBrowserOpener.isOpen,
    carePlanActionItemBrowserOpener.isOpen,
    employerBenefitBrowserOpener.isOpen,
    goalTemplateBrowserOpener.isOpen,
    history,
    taskBrowserOpener.isOpen,
    worksheetBrowserOpener.isOpen
  ]);

  return (
    <div className={styles.expertEditorContainer}>
      {loading ? (
        <IonSkeletonText animated className={styles.loaderManager} />
      ) : (
        <div className={styles.goalEditor}>
          <div className={styles.goalEditorControls}>
            <Heading2>
              <FormattedMessage
                id={goal ? "pages.carePlan.base.goals.goalModule" : "pages.carePlan.base.goals.addGoalToCarePlan"}
              />
            </Heading2>
            {goal && (
              <div className={styles.mediumFlex}>
                <ActionIconButton
                  ariaLabelKey="accessibility.ariaLabels.delete"
                  icon="trash"
                  onClick={goalDeleteOpener.open}
                />
              </div>
            )}
          </div>
          <div className={styles.titleSpacing}>
            {titleOpener.isOpen ? (
              <div>
                <GoalEditorTextField
                  canTemplate={!goal}
                  defaultValue={goal?.title}
                  goal={goal}
                  itemBrowserOpener={goalTemplateBrowserOpener}
                  journey={journey}
                  onCancel={titleOpener.close}
                  onSuccess={id => {
                    setSelectedGoalId(id);
                    titleOpener.close();
                  }}
                  templatePlaceholderKey="pages.carePlan.base.goals.searchPlaceholder"
                />
              </div>
            ) : (
              <div className={styles.goalEditorEdit}>
                <Heading2>{goal?.title}</Heading2>
                <ActionIconButton ariaLabelKey="accessibility.ariaLabels.edit" icon="edit" onClick={titleOpener.open} />
              </div>
            )}
          </div>
          {goal && (
            <>
              <GoalEditorSection
                count={goal.tasks.length}
                icon="logoIcon"
                iconColor="primary-500"
                opener={taskSectionOpener}
                titleKey="pages.carePlan.base.expertServices.add"
              >
                <GoalEditorTextField
                  goal={goal}
                  itemBrowserOpener={taskBrowserOpener}
                  journey={journey}
                  onCancel={taskSectionOpener.close}
                  onSuccess={() => {}}
                  templatePlaceholderKey="pages.carePlan.base.expertServices.searchPlaceholder"
                  type={GoalItemTypeEnum.TASK}
                />
                <div className={styles.services}>
                  {sortByPosition(goal.tasks).map(task => (
                    <ExpertService
                      handleEdit={onTaskEdit}
                      hover={false}
                      journey={journey}
                      key={task.id}
                      memberView={false}
                      task={task}
                    />
                  ))}
                </div>
              </GoalEditorSection>
              <GoalEditorSection
                count={goal.carePlanActionItems.length}
                icon="checkmark"
                iconColor="success-500"
                opener={carePlanActionItemSectionOpener}
                titleKey="pages.carePlan.base.actionItems.add"
              >
                <GoalEditorTextField
                  goal={goal}
                  itemBrowserOpener={carePlanActionItemBrowserOpener}
                  journey={journey}
                  onCancel={carePlanActionItemSectionOpener.close}
                  onSuccess={() => {}}
                  templatePlaceholderKey="pages.carePlan.base.actionItems.searchPlaceholder"
                  type={GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM}
                />
                <div className={styles.services}>
                  {goal.carePlanActionItems.map(aa => (
                    <CarePlanActionItem actionItem={aa} journey={journey} key={aa.id} onEdit={onActionItemEdit} />
                  ))}
                </div>
              </GoalEditorSection>
              <GoalEditorSection
                count={tools.length}
                icon="pencil"
                iconColor="secondary"
                opener={worksheetSectionOpener}
                titleKey="pages.carePlan.base.tools.add"
              >
                <div className={styles.editorForm}>
                  <div className={styles.topSpacing}>
                    <SearchInput
                      error=""
                      initialValue={toolSearch}
                      name="search"
                      noSpacing
                      onSearch={onToolSearch}
                      placeholder={intl.formatMessage({ id: "pages.carePlan.base.tools.searchPlaceholder" })}
                      setValue={v => setToolSearch(v)}
                    />
                  </div>
                  <ItemBrowser
                    addedIds={goal.worksheets.map(w => w.worksheetTemplate.id)}
                    handleAdd={id => {
                      worksheetCreate({
                        variables: {
                          goalId: goal.id,
                          journeyId: journey.id,
                          worksheetTemplateId: id
                        }
                      });
                    }}
                    handleRemove={worksheetTemplateId => {
                      const worksheet = goal.worksheets.find(
                        worksheet => worksheet.worksheetTemplate.id === worksheetTemplateId
                      );
                      if (worksheet) {
                        worksheetDestroy({ id: worksheet.id });
                      }
                    }}
                    itemType={GoalItemTypeEnum.WORKSHEET}
                    journeyId={journey.id}
                    opener={worksheetBrowserOpener}
                    skipCloseOnSubmit
                  />

                  <div className={styles.toolsEditor}>
                    {tools.map(tool => (
                      <Tool
                        archived={goal.archived}
                        goal={goal}
                        handleRemove={handleToolRemove}
                        journeyId={journey.id}
                        key={tool.type ?? tool.worksheet?.id}
                        legalOpener={legalOpener}
                        medicationTrackerOpener={medicationTrackerOpener}
                        providerOpener={providerOpener}
                        tool={tool}
                      />
                    ))}
                  </div>
                </div>
              </GoalEditorSection>

              <GoalEditorSection
                count={goal.articles.length}
                icon="globe"
                iconColor="danger-500"
                opener={articleSectionOpener}
                titleKey="pages.carePlan.base.articles.add"
              >
                <div className={styles.editorForm}>
                  <div className={styles.topSpacing}>
                    <SearchInput
                      error={""}
                      initialValue={articleSearch}
                      name="search"
                      noSpacing
                      onSearch={searchArticles}
                      placeholder={intl.formatMessage({ id: "pages.carePlan.base.articles.searchPlaceholder" })}
                      setValue={setArticleSearch}
                    />
                  </div>
                  <ItemBrowser
                    addedIds={goal.articles.map(a => a.id)}
                    handleAdd={id => {
                      addGoalItem({ articleId: id, goalId: goal.id, itemType: GoalItemTypeEnum.ARTICLE });
                    }}
                    handleRemove={handleArticleRemove}
                    itemType={GoalItemTypeEnum.ARTICLE}
                    journeyId={journey.id}
                    opener={articleBrowserOpener}
                    skipCloseOnSubmit
                  />
                  <div className={styles.articles}>
                    {sortByTitle(goal.articles).map(article => (
                      <ArticleCard
                        article={article}
                        goal={goal}
                        journeyId={journey.id}
                        key={article.id}
                        onRemove={handleArticleRemove}
                      />
                    ))}
                  </div>
                </div>

                <div className={styles.editorForm}>
                  <div className={styles.topSpacing}>
                    <SearchInput
                      error={""}
                      initialValue={employerBenefitSearch}
                      name="marketplaceSearch"
                      noSpacing
                      onSearch={onEmployerBenefitSearch}
                      placeholder={intl.formatMessage({ id: "pages.carePlan.base.employerBenefit.searchPlaceholder" })}
                      setValue={setEmployerBenefitSearch}
                    />
                  </div>
                  <ItemBrowser
                    addedIds={goal.employerBenefits.map(eb => eb.id)}
                    handleAdd={id => {
                      addGoalItem({
                        employerBenefitId: id,
                        goalId: goal.id,
                        itemType: GoalItemTypeEnum.EMPLOYER_BENEFIT
                      });
                    }}
                    handleRemove={handleEmployerBenefitRemove}
                    itemType={GoalItemTypeEnum.EMPLOYER_BENEFIT}
                    journeyId={journey.id}
                    opener={employerBenefitBrowserOpener}
                    skipCloseOnSubmit
                  />
                  <div className={styles.employerBenefits}>
                    {goal.employerBenefits.map(employerBenefit => (
                      <EmployerBenefitCard
                        employerBenefit={employerBenefit}
                        goal={goal}
                        journeyId={journey.id}
                        key={employerBenefit.id}
                        layout="vertical"
                        onRemove={handleEmployerBenefitRemove}
                      />
                    ))}
                  </div>
                </div>
              </GoalEditorSection>
            </>
          )}
        </div>
      )}
      <Modal
        opener={actionItemOpener}
        ref={actionItemModalRef}
        title={intl.formatMessage({
          id: actionItemBeingEdited
            ? "models.carePlanActionItem.editClientTask"
            : "models.carePlanActionItem.addClientTask"
        })}
      >
        {({ modalDisplayed }) => (
          <CarePlanActionItemForm
            autofocus={modalDisplayed}
            carePlanActionItem={actionItemBeingEdited}
            goal={goal}
            journey={journey}
            onCancel={actionItemOpener.close}
            onError={handleActionItemError}
            onSuccess={actionItemOpener.close}
          />
        )}
      </Modal>
      <Modal
        opener={taskOpener}
        ref={taskModalRef}
        title={intl.formatMessage({
          id: taskBeingEdited ? "models.tasks.editTask" : "models.tasks.addTask"
        })}
      >
        {({ modalDisplayed }) => (
          <TaskForm
            autofocus={modalDisplayed}
            clientVisibleTaskForm
            journey={journey}
            onCancel={taskOpener.close}
            onSuccess={taskOpener.close}
            primaryExpertId={journey.primaryExpert?.id}
            task={taskBeingEdited}
          />
        )}
      </Modal>
      <LegalDocumentTracker journey={journey} legalDocumentsOpener={legalOpener} />
      <ProviderTracker
        batchedProviders={batchedProviders}
        journey={journey}
        providerTrackerOpener={providerOpener}
        restartWizard
      />
      <MedicationTracker
        batchedMedications={batchedMedications}
        journey={journey}
        medicationTrackerOpener={medicationTrackerOpener}
        restartWizard
      />
      <DeleteConfirmationAlert
        headerKey="pages.carePlan.base.goals.reallyDelete"
        onDelete={handleGoalDelete}
        opener={goalDeleteOpener}
      />
    </div>
  );
};

export default GoalEditor;
